import React, { useState, useEffect } from "react";
import { primaryColor, secondaryColor } from "../../utils/comon";
import ProfileDropdown from "./ProfileDropdown";
import LogoutModal from "../pages/Profile/LogoutModal";
import UpdatePasswordModal from "../pages/Profile/UpdatePasswordModal";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../app/features/authSlice";
import storageService from "../../utils/storageService";

const Header = ({ toggleSidebar, isSidebarOpen, isDarkMode, setIsDarkMode }) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);

  // Mirror exactly what the Angular header does:
  // Read employeeName, roleName and userImage from encrypted localStorage
  const [displayName, setDisplayName] = useState('');
  const [displayRole, setDisplayRole] = useState('');
  const [displayImage, setDisplayImage] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { profileData } = useSelector((state) => state.profile);

  // Re-read user data from localStorage whenever auth user changes (i.e. after login)
  useEffect(() => {
    const name = storageService.getItem('employeeName');
    const role = storageService.getItem('roleName');
    const image = storageService.getItem('userImage');
    setDisplayName(name || '');
    setDisplayRole(role || '');
    setDisplayImage(image || null);
  }, [user]);

  // If localStorage values are empty, fall back to profileData once it loads
  useEffect(() => {
    if (!displayName && profileData) {
      const name = `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();
      if (name) setDisplayName(name);
    }
    if (!displayRole && profileData) {
      const role = profileData?.roles?.length > 0
        ? profileData.roles.map(r => r.roleName).join(', ')
        : profileData?.role || '';
      if (role) setDisplayRole(role);
    }
    if (!displayImage && profileData?.userImages?.[0]?.image) {
      setDisplayImage(profileData.userImages[0].image);
    }
    if (!displayImage && (user?.Image || user?.image)) {
      setDisplayImage(user.Image || user.image);
    }
  }, [profileData, user, displayImage]);

  // Final safe fallbacks for rendering
  const resolvedName = displayName || 'Administrator';
  const resolvedRole = displayRole || 'Admin';
  const resolvedImage = displayImage || null;

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Logged out successfully");
    setLogoutModalOpen(false);
    navigate("/login");
  };

  return (
    <header className="h-14 flex items-center justify-between bg-dashboardbg border-b border-bordercolor px-4 lg:px-6 relative z-50">
      {/* Left Section (Mobile Menu Toggle + Logo) */}
      <div className="flex items-center gap-4">
        <button onClick={toggleSidebar} className="lg:hidden text-textprimary hover:text-textsecondary transition-colors">
          <span className="material-symbols-outlined">menu</span>
        </button>
        <div className="z-20 cursor-pointer" onClick={() => window.open("https://myhello.in/", "_blank")}>
          <img
            src="/hellologo.svg"
            alt="Hello"
            className="h-4"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4 relative">
        {/* Notice/Complaint Shortcut */}
        <button
          onClick={() => navigate("/zentcomm/complaint-management/add")}
          className="w-9 h-9 flex items-center justify-center rounded-full text-red-500 bg-white dark:bg-gray-800 shadow-md hover:shadow-lg hover:scale-105 transition-all"
          title="Add Complaint / Notice"
        >
          <span className="material-symbols-outlined !text-[20px]">
            campaign
          </span>
        </button>

        {/* Theme Toggle Button */}
        <button
          onClick={() => setIsDarkMode(prev => !prev)}
          className="w-9 h-9 flex items-center justify-center rounded-full text-textsecondary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Toggle Theme"
        >
          <span className="material-symbols-outlined !text-xl">
            {isDarkMode ? "light_mode" : "dark_mode"}
          </span>
        </button>

        {/* User Name & Role */}
        <div className="hidden md:flex flex-col items-end mr-2">
          <span className="text-sm font-bold text-textprimary">
            {resolvedName}
          </span>
          <span className="text-[11px] text-textsecondary font-medium uppercase tracking-wider">
            {resolvedRole}
          </span>
        </div>

        {/* Profile Circle */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!isDropdownOpen)}
            className="relative w-9 h-9 border flex items-center justify-center rounded-full text-xs font-bold transition-transform active:scale-95 shadow-sm overflow-hidden bg-gray-50"
            style={{ borderColor: primaryColor() + '20' }}
          >
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(resolvedName)}&background=${secondaryColor()?.replace('#', '')}&color=${primaryColor()?.replace('#', '')}&bold=true`}
              alt="Avatar"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {resolvedImage && resolvedImage !== 'null' && resolvedImage !== 'undefined' && resolvedImage.trim() !== '' && (
              <img
                src={resolvedImage}
                alt="Avatar"
                className="absolute inset-0 w-full h-full object-cover z-10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </button>

          {/* Profile Dropdown */}
          <ProfileDropdown
            isOpen={isDropdownOpen}
            onClose={() => setDropdownOpen(false)}
            onLogout={() => setLogoutModalOpen(true)}
            onUpdatePassword={() => setPasswordModalOpen(true)}
            userData={profileData}
            displayName={resolvedName}
            displayRole={resolvedRole}
            displayImage={resolvedImage}
          />
        </div>
      </div>

      {/* Modals */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />
      <UpdatePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </header>
  );
};

export default Header;
